<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <div>
          <h6 class="mb-0">Regularizar venda cancelada liquidada</h6>
          <small class="modal-subtitle">
            Registre no financeiro uma venda que foi liquidada pela operadora após o cancelamento da matrícula.
          </small>
        </div>

        <i
          class="bi bi-x-circle close-icon"
          @click="$emit('close')"
        ></i>
      </div>

      <div class="modal-content">
        <div class="alert alert-warning py-2 mb-3">
          <strong>Atenção:</strong>
          o pagamento original da matrícula continuará cancelado.
          Esta operação registrará somente o fato financeiro ocorrido no repasse da operadora.
        </div>

        <div class="info-box mb-3">
          <div class="info-row">
            <span class="info-label">Item da operadora:</span>
            <span>#{{ item?.id || '—' }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Autorização:</span>
            <span>{{ item?.autorizacao || '—' }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Data do repasse:</span>
            <span>{{ formatDate(item?.data_vencimento || item?.data_venda) }}</span>
          </div>
        </div>

        <div class="finance-summary">
          <div class="finance-card">
            <span class="finance-label">Receita a registrar</span>
            <strong>{{ formatCurrency(valorBruto) }}</strong>
            <small>Outras Receitas · Compensação no repasse</small>
          </div>

          <div class="finance-card">
            <span class="finance-label">Taxa da operadora</span>
            <strong>-{{ formatCurrency(valorTaxa) }}</strong>
            <small>Gerada automaticamente</small>
          </div>

          <div class="finance-card is-highlight">
            <span class="finance-label">Impacto líquido</span>
            <strong>{{ formatCurrency(valorLiquido) }}</strong>
            <small>Valor efetivamente recebido no repasse</small>
          </div>
        </div>

        <div
          v-if="item?.motivo_pendencia"
          class="context-box mt-3"
        >
          {{ item.motivo_pendencia }}
        </div>
      </div>

      <div class="modal-footer">
        <button
          class="btn btn-secondary btn-sm me-2"
          type="button"
          :disabled="salvando"
          @click="$emit('close')"
        >
          Cancelar
        </button>

        <button
          class="btn btn-primary btn-sm"
          type="button"
          :disabled="salvando"
          @click="$emit('confirmar')"
        >
          {{ salvando ? 'Regularizando...' : 'Confirmar regularização' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  item: { type: Object, default: null },
  salvando: { type: Boolean, default: false }
})

defineEmits(['close', 'confirmar'])

const valorBruto = computed(() =>
  Math.abs(
    Number(
      props.item?.valor_parcela ??
      props.item?.valor_bruto ??
      0
    )
  )
)

const valorLiquido = computed(() =>
  Math.abs(Number(props.item?.valor_liquido || 0))
)

const valorTaxa = computed(() => {
  const diferenca = Math.abs(
    Number(props.item?.diferenca_bruto_liquido || 0)
  )

  if (diferenca > 0) return diferenca

  return Math.max(
    0,
    valorBruto.value - valorLiquido.value
  )
})

function formatDate(v) {
  if (!v) return '—'

  const s = String(v).slice(0, 10)

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-')
    return `${d}/${m}/${y}`
  }

  return '—'
}

function formatCurrency(v) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(v || 0))
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  margin: 0 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  background: #f3f4f6;
  padding: 10px 15px;
  border-bottom: 1px solid #ced4da;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
}

.close-icon {
  cursor: pointer;
  font-size: 1.25rem;
}

.modal-content {
  padding: 15px;
  overflow-y: auto;
}

.modal-footer {
  padding: 10px 15px;
  border-top: 1px solid #ced4da;
  display: flex;
  justify-content: flex-end;
}

.info-box,
.context-box {
  border: 1px solid #d1d5db;
  padding: 12px;
  background: #f9fafb;
}

.info-row {
  display: flex;
  gap: 8px;
  margin-bottom: 5px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  min-width: 140px;
}

.finance-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.finance-card {
  border: 1px solid #d1d5db;
  padding: 12px;
  display: flex;
  flex-direction: column;
}

.finance-card strong {
  font-size: 1.15rem;
  margin: 4px 0;
}

.finance-card small,
.finance-label {
  color: #6b7280;
  font-size: 0.78rem;
}

.finance-card.is-highlight {
  background: #ecfdf5;
  border-color: #86efac;
}

.context-box {
  font-size: 0.82rem;
  line-height: 1.35;
}

@media (max-width: 700px) {
  .finance-summary {
    grid-template-columns: 1fr;
  }
}
</style>
